/**
 * The CAP's history, and the challenge where the next person needs it
 * (C1).
 *
 * Every transition has always been recorded and hash-chained, comment
 * and all -- and shown to nobody. The reviewer's send-back comment is
 * MANDATORY, so the product insisted he explain himself and then never
 * repeated it: the preparer saw "Sent back" and a Withdraw button, and
 * the approver signed without ever seeing the challenge.
 */
import { historyVerb } from "framework/historyVerb";
import { useTranslation } from "react-i18next";

import { useCapTransitions } from "framework/api/hooks";
import type { CAPTransition } from "shared/api/types";

/** The most recent send-back, if the CAP carries one. */
export function latestChallenge(
  transitions: CAPTransition[] | undefined,
): CAPTransition | null {
  // Array.isArray, not `?? []`: an unexpected payload shape would
  // otherwise throw inside render, and with no error boundary above the
  // CAP page that takes the whole page down. Same guard as P76/P362.
  const rows = Array.isArray(transitions) ? transitions : [];
  const backs = rows.filter(
    (t) => t.to_state === "sent_back" && (t.comment ?? "").trim() !== "",
  );
  return backs.length > 0 ? (backs[backs.length - 1] ?? null) : null;
}

function actorName(t: CAPTransition): string {
  return t.actor_display_name || t.actor_username || t.actor_position;
}

/**
 * The open challenge, at the top of the page.
 *
 * Shown while the CAP still carries an unanswered send-back, and to
 * the approver afterwards -- he is the one being asked to sign over it.
 */
export function ChallengeBanner({
  capId,
  capState,
}: {
  capId: string;
  capState: string;
}) {
  const { t } = useTranslation();
  const transitions = useCapTransitions(capId);
  const challenge = latestChallenge(transitions.data);
  if (!challenge) return null;
  const open = capState === "sent_back";
  return (
    <div
      data-testid="cap-challenge-banner"
      className={`rounded border p-3 text-sm ${
        open
          ? "border-amber-300 bg-amber-50 text-amber-950"
          : "border-slate-200 bg-slate-50 text-slate-700"
      }`}
    >
      <p className="font-medium">
        {open
          ? t("caps.detail.history.challengeOpen", { who: actorName(challenge) })
          : t("caps.detail.history.challengePast", { who: actorName(challenge) })}
      </p>
      <p className="mt-1 italic">“{challenge.comment}”</p>
    </div>
  );
}

export function CapHistory({ capId }: { capId: string }) {
  const { t } = useTranslation();
  const transitions = useCapTransitions(capId);
  const rows = Array.isArray(transitions.data) ? transitions.data : [];

  return (
    <section className="mt-6" data-testid="cap-history">
      <h2 className="mb-2 text-sm font-semibold text-slate-700">
        {t("caps.detail.history.heading")}
      </h2>
      {transitions.isLoading && (
        <p className="text-sm text-slate-400">{t("caps.detail.history.loading")}</p>
      )}
      {!transitions.isLoading && rows.length === 0 && (
        <p className="text-sm text-slate-500">{t("caps.detail.history.empty")}</p>
      )}
      <ol className="space-y-2">
        {rows.map((tr) => (
          <li
            key={tr.id}
            data-testid={`cap-history-${tr.id}`}
            className="rounded border border-slate-200 bg-white px-3 py-2"
          >
            <div className="flex flex-wrap items-baseline gap-x-2 text-sm">
              <span className="font-medium text-slate-900">{actorName(tr)}</span>
              <span className="text-slate-600">
                {/* D1 -- what they DID, not the state it landed in. */}
                {t(`caps.detail.history.did.${historyVerb(tr)}`, {
                  position: tr.to_position,
                  defaultValue: tr.to_state,
                })}
              </span>
              <span className="ms-auto font-mono text-[11px] text-slate-400">
                {tr.captured_at.slice(0, 16).replace("T", " ")}
              </span>
            </div>
            {tr.comment && (
              <p className="mt-1 text-sm italic text-slate-700">“{tr.comment}”</p>
            )}
          </li>
        ))}
      </ol>
    </section>
  );
}
